import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Login } from '../routes/login';
import { Exception } from '../components';
import { useAuthContext } from '../contexts/AuthContext';
import PostPwdForm from '../routes/login/PostPwdForm';
import { Confirmation } from '../routes/emailConfirmation/Confirmation';
import { UserTheme } from '../assets/styles/Theme/UserTheme';
import { RequireAuth } from './RequireAuth';
import { Maintenance } from '../routes/maintenance/Maintenance';
import { MAINTENANCE_MODE } from '../utils/constants/maintenance';
import { ProfileRouter as DefaultProfileRouter } from '../routes/usersShows/profile/ProfileRouter';
import { ShowSettings } from '../routes/usersShows/settings/ShowSettings';
/**
 * BaseUserRouter component.
 * @component
 *
 * Shared router component that handles common routing logic for owner, pensioner, and user routers.
 * @param {Object} props - Component props.
 * @param {Object} props.routes - Routes configuration object.
 * @param {Object} props.outOfNavRoutes - Out of navigation routes configuration object.
 * @param {ReactElement} props.dashboardComponent - Dashboard component to render.
 * @param {string} props.dashboardPath - Path for dashboard route (if null, uses index route).
 * @param {ReactElement} props.profileComponent - Profile component to render (defaults to ProfileRouter from usersShows).
 * @param {ReactElement} props.settingsComponent - Settings component to render (defaults to ShowSettings).
 * @param {Array<ReactElement>} props.additionalRoutes - Additional route elements specific to the router.
 * @returns {ReactElement} The BaseUserRouter component.
 */
export const BaseUserRouter = ({
  outOfNavRoutes,
  dashboardComponent,
  dashboardPath = null,
  profileComponent = <DefaultProfileRouter />,
  settingsComponent = <ShowSettings />,
  additionalRoutes = []
}) => {
  const { isValid } = useAuthContext();

  useEffect(() => {
    const body = document.body;
    if (body) {
      body.className = 'user';
    }
  }, []);

  return (
    <BrowserRouter>
      <UserTheme />
      <Routes>
        <Route path={outOfNavRoutes.LOGIN} element={<Login />} />
        <Route
          path={`${outOfNavRoutes.PASSWORD_CREATION}/:urlToken`}
          element={<PostPwdForm />}
        />
        <Route
          path={`${outOfNavRoutes.CONFIRMATION}/:token`}
          element={<Confirmation />}
        />
        <Route path={outOfNavRoutes.MAINTENANCE} element={<Maintenance />} />
        {MAINTENANCE_MODE && !isValid && (
          <Route index element={<Maintenance />} />
        )}
        <Route element={<RequireAuth isValid={isValid} />}>
          <Route
            path={`${outOfNavRoutes.PROFILE}/*`}
            element={profileComponent}
          />
          <Route path={outOfNavRoutes.SETTINGS} element={settingsComponent} />
          {dashboardPath ? (
            <Route path={dashboardPath} element={dashboardComponent} />
          ) : (
            <Route index element={dashboardComponent} />
          )}
          {additionalRoutes}
          <Route path="*" element={<Exception />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

BaseUserRouter.propTypes = {
  outOfNavRoutes: PropTypes.shape({
    LOGIN: PropTypes.string.isRequired,
    CONFIRMATION: PropTypes.string.isRequired,
    MAINTENANCE: PropTypes.string.isRequired,
    PROFILE: PropTypes.string.isRequired,
    SETTINGS: PropTypes.string.isRequired,
    PASSWORD_CREATION: PropTypes.string.isRequired
  }).isRequired,
  dashboardComponent: PropTypes.element.isRequired,
  dashboardPath: PropTypes.string.isRequired,
  profileComponent: PropTypes.element.isRequired,
  settingsComponent: PropTypes.element.isRequired,
  additionalRoutes: PropTypes.arrayOf(PropTypes.element).isRequired
};
